/**
 * What people are carrying.
 *
 * One table, because the interesting property is comparative: a guard has a
 * radio and a keycard, a worker has a phone and a wallet, the janitor has the
 * keys to everything and nothing worth money. That contrast is the whole design
 * of pickpocketing — you rob the guard for ACCESS and the worker for CASH, and
 * you rob either of their comms first if you intend to be noticed.
 *
 * Seeded from the building, so the same seed always produces the same pockets.
 * Pure + headless (rule 4).
 */

import { createKey, createPhone, createRadio, createValuable, createMop, type Item } from './inventory';
import type { Guard } from './guard';
import type { MapData } from './map';

export type Carrier = 'guard' | 'captain' | 'janitor' | 'civilian' | 'escortee';

/** A door key for a random locked door on this floor, or null if none are locked. */
function floorKeycard(map: MapData, rng: () => number): Item | null {
  const locked = map.doors.filter((d) => d.lockId !== null);
  if (locked.length === 0) return null;
  const pick = locked[Math.floor(rng() * locked.length)]!;
  return createKey(pick.lockId!, 'Keycard');
}

/**
 * Fill one entity's pockets. `map` is only read for keycards, so a caller
 * without a generated floor (the tutorial) still gets sensible contents.
 */
export function fillPockets(who: Carrier, map: MapData, rng: () => number): Item[] {
  const out: Item[] = [];
  switch (who) {
    case 'captain':
    case 'guard': {
      out.push(createRadio()); // the thing worth taking first
      const card = floorKeycard(map, rng);
      if (card) out.push(card);
      if (rng() < 0.5) out.push(createValuable('Loose change', 40 + Math.floor(rng() * 60)));
      break;
    }
    case 'janitor': {
      // Access to everywhere, nothing worth money — and no radio, which is why
      // a janitor has always had to run to tell someone.
      const card = floorKeycard(map, rng);
      if (card) out.push(card);
      out.push(createMop());
      break;
    }
    case 'civilian': {
      if (rng() < 0.85) out.push(createPhone());
      out.push(createValuable('Wallet', 60 + Math.floor(rng() * 180)));
      if (rng() < 0.3) {
        const card = floorKeycard(map, rng);
        if (card) out.push(card);
      }
      break;
    }
    case 'escortee':
      // They're the objective, not a mark.
      break;
  }
  return out;
}

/** Give `entity` its pockets in place. */
export function equipPockets(entity: Guard, who: Carrier, map: MapData, rng: () => number): void {
  entity.pockets = fillPockets(who, map, rng);
}

/** Does this entity still have working comms on them? Drives what they do about
 *  being robbed — see `sim/pickpocket.ts`. */
export function hasComms(entity: Guard): 'radio' | 'phone' | null {
  if (entity.pockets.some((it) => it.kind === 'radio')) return 'radio';
  if (entity.pockets.some((it) => it.kind === 'phone')) return 'phone';
  return null;
}
